import InputError from '@/components/input-error';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { store } from '@/routes/login';
import { Form, Head } from '@inertiajs/react';
import { Lock, Mail, ShieldCheck } from 'lucide-react';

interface AdminLoginProps {
    status?: string;
}

export default function AdminLogin({ status }: AdminLoginProps) {
    return (
        <div className="flex min-h-svh items-center justify-center bg-muted p-6">
            <Head title="Admin Sign in" />

            <div className="w-full max-w-md rounded-2xl border border-border bg-background px-10 py-8 shadow-xl">
                <div className="mb-6 flex flex-col items-center gap-3 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-accent">
                        <ShieldCheck className="h-6 w-6 text-foreground" />
                    </div>
                    <h1 className="text-xl font-semibold text-foreground">
                        Admin Sign in
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        Internal operators only.
                    </p>
                </div>

                {status && (
                    <div className="mb-4 text-center text-sm font-medium text-emerald-400">
                        {status}
                    </div>
                )}

                <Form
                    {...store.form()}
                    resetOnSuccess={['password']}
                    className="space-y-4"
                >
                    {({ processing, errors }) => (
                        <>
                            <div className="space-y-2">
                                <Label
                                    htmlFor="email"
                                    className="text-sm font-medium text-muted-foreground"
                                >
                                    Email address
                                </Label>
                                <div className="group relative">
                                    <Mail className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        id="email"
                                        type="email"
                                        name="email"
                                        required
                                        autoFocus
                                        tabIndex={1}
                                        autoComplete="email"
                                        placeholder="admin@example.com"
                                        className="h-11 border-border bg-muted pl-10 text-foreground placeholder:text-muted-foreground"
                                    />
                                </div>
                                <InputError message={errors.email} />
                            </div>

                            <div className="space-y-2">
                                <Label
                                    htmlFor="password"
                                    className="text-sm font-medium text-muted-foreground"
                                >
                                    Password
                                </Label>
                                <div className="group relative">
                                    <Lock className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        id="password"
                                        type="password"
                                        name="password"
                                        required
                                        tabIndex={2}
                                        autoComplete="current-password"
                                        placeholder="Password"
                                        className="h-11 border-border bg-muted pl-10 text-foreground placeholder:text-muted-foreground"
                                    />
                                </div>
                                <InputError message={errors.password} />
                            </div>

                            <div className="flex items-center gap-3 pt-1">
                                <Checkbox
                                    id="remember"
                                    name="remember"
                                    tabIndex={3}
                                />
                                <Label
                                    htmlFor="remember"
                                    className="text-sm text-muted-foreground"
                                >
                                    Remember me
                                </Label>
                            </div>

                            <Button
                                type="submit"
                                className="mt-2 h-11 w-full rounded-lg bg-secondary text-sm font-semibold text-secondary-foreground hover:bg-secondary/80"
                                tabIndex={4}
                                disabled={processing}
                                data-test="admin-login-button"
                            >
                                {processing && <Spinner />}
                                Sign in
                            </Button>
                        </>
                    )}
                </Form>
            </div>
        </div>
    );
}
